/**
 * La capa de datos de Smarty Drop: el catálogo, los encargos, la billetera y los cobros.
 *
 * ── POR QUÉ ESTE ARCHIVO EXISTE ────────────────────────────────────────────────────────────────
 * Todo esto vivía dentro de `src/lib/operations/ordersApi.ts`, que con 4.124 líneas es el archivo
 * que más conflictos de merge genera del repo —171 commits en seis meses— y el que el trinquete de
 * tamaño tiene congelado. La auditoría del módulo (22-sep-2026) lo marcó: la capa de datos de Drop
 * no tenía nada que hacer ahí.
 *
 * ⭐ NO ES UN ARCHIVO NUEVO POR ORDEN, ES POR DUEÑO. `ordersApi` es el pedido de CUALQUIER negocio;
 *    esto es el vínculo entre dos negocios distintos —revendedor y almacén— con su propia billetera
 *    y su propio libro. Son dos temas que cambian por motivos distintos, y por eso cambiarlos
 *    juntos duele.
 *
 * 🪤 EL CÓDIGO SE MOVIÓ TAL CUAL, a propósito: un refactor que además cambia comportamiento no se
 *    puede revisar, porque cualquier diferencia se puede achacar al movimiento. Lo único que cambió
 *    son los imports.
 */
import { supabase } from '@/lib/supabase';
import { importFromSupplier, type SupplierImportResult } from '@/lib/operations/importarDelProveedor';

// ---- Smarty Drop: el catálogo propio ----------------------------------------
// A diferencia de Dropi y Aliclik, acá no hay API ajena ni token: los proveedores
// son tenants de Smarty y el catálogo es una consulta a nuestra propia base. Por
// eso el stock que se ve es el vigente y no una copia sincronizada por cron — que
// es justo lo que fabricaría el problema que más duele en este modelo: el
// revendedor descubriendo que no hay stock DESPUÉS de haberle vendido a su
// cliente.

/** Una fila del catálogo mayorista, tal como la devuelve `fn_drop_catalogo`. */
export interface DropCatalogoRow {
  catalogo_id: string;
  proveedor_id: string;
  proveedor: string;
  proveedor_logo: string | null;
  condiciones: string | null;
  cobertura: string | null;
  contacto_wa: string | null;
  /** Lo que cuesta llevar UN pedido a la agencia. Por pedido, no por producto. */
  costo_despacho: number;
  /**
   * ⭐ A QUÉ EMPRESA SE LE COMPRA. Razón social y RUC salen de `tenant_business_profile`, donde ya
   * estaban desde la consolidación de datos de empresa; hasta el 23-sep no llegaban a la pantalla.
   * Quien manda plata por adelantado tiene derecho a saber a quién. Nulos mientras el proveedor no
   * complete sus datos: la ficha simplemente no muestra la sección.
   */
  razon_social: string | null;
  ruc: string | null;
  /**
   * De qué almacén sale el paquete. Hoy el proveedor ES su almacén, pero viaja aparte desde ya para
   * que el día que sean dos entidades distintas (F-F) la pantalla no tenga que cambiar.
   */
  almacen: string | null;
  /** Carpeta con fotos y videos de venta de ESTE producto, publicada por el proveedor. Un enlace. */
  material_url: string | null;
  product_id: string;
  nombre: string;
  descripcion: string | null;
  /** La portada. Es la que va en las tarjetas de la grilla. */
  imagen: string | null;
  /**
   * ⭐ TODAS LAS FOTOS, para el detalle. Los productos del catálogo tienen seis cada uno y la de
   * beneficios es literalmente el argumento de venta que el revendedor va a copiar a su
   * publicación. Mostrarle sólo la portada lo obligaba a pedirlas por WhatsApp.
   */
  imagenes: string[];
  categoria: string | null;
  sku: string | null;
  stock: number;
  /** Lo que paga ESTE drop: su precio especial si tiene uno (20261021203000), si no el general. */
  precio_mayorista: number;
  precio_sugerido: number | null;
  /** El precio de lista. Distinto de `precio_mayorista` sólo cuando este drop tiene uno especial. */
  precio_general?: number;
  precio_especial?: boolean;
  /** El sello de verificado de su proveedor (20260928130000), y lo que Smarty comprobó. */
  proveedor_verificado?: boolean;
  proveedor_verificado_nota?: string | null;
  /**
   * ⭐ ESTA FILA ES DE MI PROPIO NEGOCIO. El proveedor ve su estante en la misma pantalla que el
   * revendedor ve el catálogo — no puede pedírselo a sí mismo, pero sí prenderlo y apagarlo.
   */
  es_mio: boolean;
  /** Si está publicado. Sólo puede venir en `false` en las filas propias. */
  publicado: boolean;
  /**
   * ⭐ PRUEBA SOCIAL, QUE ES LO QUE FALTA PARA DECIDIR. Entre dos productos con el mismo margen, el
   * que quince personas ya venden no es una apuesta: es un producto probado. El número existía desde
   * que existe el módulo y sólo se mostraba sumado por proveedor, que es el dato del vendedor y no
   * el del producto.
   */
  lo_venden: number;
  /** Encargos que lo incluyeron en 30 días. Movimiento real, no intención. */
  pedidos_30d: number;
  /**
   * De la ficha técnica del producto (`product_attributes`), que hasta el 2-oct no llegaba al
   * catálogo: el revendedor tenía que preguntar las dos cosas por WhatsApp.
   *
   * `Importado` o `Nacional`, tal como lo cargó el proveedor. Nulo mientras no lo cargue.
   */
  procedencia?: string | null;
  /**
   * 🔒 SI TIENE REGISTRO SANITARIO — NO EL NÚMERO. Un booleano a propósito: el número identifica al
   * TITULAR del registro, y un revendedor que lo tiene entra a DIGEMID y llega directo al
   * fabricante. No es un dato secreto (ese registro es público), pero publicarlo le acorta el camino
   * para saltearse al proveedor, así que el número NO SALE DE LA BASE. Enmascararlo acá no servía:
   * lo que viaja al navegador ya está entregado.
   */
  con_registro_sanitario?: boolean;
}

/** Los tres catálogos que muestra la misma pantalla. El favorito guarda de cuál es. */
export type PlataformaDrop = 'smartydrop' | 'dropi' | 'aliclik';

/**
 * Los favoritos de QUIEN ESTÁ MIRANDO, en esta plataforma.
 *
 * ⭐ ES DE LA PERSONA, NO DEL NEGOCIO. La tabla guarda `user_id` además de `tenant_id`, y las
 *    políticas exigen los dos. En una tienda con tres personas, lo que le interesa a una no es lo
 *    que le interesa a otra; una lista común termina siendo el cajón de todos y de nadie.
 *
 * 🪤 EL `user_id` NO VIAJA DESDE LA PANTALLA. Se pide acá, a la sesión. Aceptarlo como argumento
 *    sería ofrecer que alguien mande el de otro, y aunque la policy lo rechace, un parámetro que
 *    sólo puede tener un valor correcto no debería existir.
 */
export async function fetchFavoritos(tenantId: string, plataforma: PlataformaDrop): Promise<string[]> {
  const { data: sesion } = await supabase.auth.getUser();
  const userId = sesion.user?.id;
  if (!userId) return [];

  const { data, error } = await supabase
    .from('drop_favoritos')
    .select('clave')
    .eq('tenant_id', tenantId)
    .eq('user_id', userId)
    .eq('plataforma', plataforma)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((f) => f.clave as string);
}

/**
 * Pone o saca un favorito. Devuelve cómo quedó, para que la pantalla no tenga que adivinarlo.
 *
 * 🪤 SE BORRA E INSERTA, NO SE «ALTERNA» EN LA BASE. La pantalla ya sabe si estaba marcado; hacerle
 *    preguntar primero agregaría un viaje de ida y vuelta a cada clic sobre un corazón, que es la
 *    interacción que más rápido tiene que responder de toda la pantalla.
 */
export async function marcarFavorito(params: {
  tenantId: string;
  plataforma: PlataformaDrop;
  clave: string;
  favorito: boolean;
}): Promise<void> {
  const { data: sesion } = await supabase.auth.getUser();
  const userId = sesion.user?.id;
  if (!userId) throw new Error('Hay que iniciar sesión para guardar favoritos.');

  if (params.favorito) {
    const { error } = await supabase
      .from('drop_favoritos')
      .upsert({
        tenant_id: params.tenantId,
        user_id: userId,
        plataforma: params.plataforma,
        clave: params.clave,
      }, { onConflict: 'tenant_id,user_id,plataforma,clave', ignoreDuplicates: true });
    if (error) throw error;
    return;
  }

  const { error } = await supabase
    .from('drop_favoritos')
    .delete()
    .eq('tenant_id', params.tenantId)
    .eq('user_id', userId)
    .eq('plataforma', params.plataforma)
    .eq('clave', params.clave);
  if (error) throw error;
}

/**
 * Las filas del espejo de Dropi que corresponden a estas claves de favorito.
 *
 * ⭐ POR QUÉ DROPI NECESITA SU PROPIA CONSULTA. Smarty Drop y Aliclik traen su catálogo entero en
 *    una llamada, así que marcar los favoritos es cruzar dos listas que ya están en memoria. El
 *    espejo de Dropi tiene decenas de miles de filas y se pagina en el servidor: los favoritos de
 *    alguien casi nunca están en la página que se está viendo. Sin esto, la pestaña «Favoritos»
 *    mostraría sólo los que hubiera de casualidad en pantalla, que es peor que no tenerla.
 *
 * 🪤 VA ACÁ Y NO EN `ordersApi`: ese archivo pasa las 4.000 líneas y el trinquete de tamaño lo tiene
 *    congelado. La capa de Drop ya vive en este archivo desde el refactor del 22-sep.
 */
export async function fetchDropiPorClaves(claves: string[]): Promise<DropiFilaEspejo[]> {
  const ids = claves
    .filter((c) => c.startsWith('dropi-'))
    .map((c) => Number(c.slice('dropi-'.length)))
    .filter((n) => Number.isFinite(n));
  if (ids.length === 0) return [];

  const { data, error } = await supabase
    .from('dropi_marketplace')
    .select('dropi_product_id,name,sku,description,image,suggested_price,cost_price,stock,category,supplier_name,variations')
    .eq('is_active', true)
    // 🪤 PostgREST corta en 1.000 filas. Una lista de favoritos jamás llega ahí, pero el tope se
    //    pide igual para que el día que alguien guarde 2.000 la pantalla no mienta en silencio.
    .in('dropi_product_id', ids.slice(0, 1000));
  if (error) throw error;
  return (data ?? []) as DropiFilaEspejo[];
}

/** La forma de una fila del espejo de Dropi, tal como la consume la pantalla del catálogo. */
export type DropiFilaEspejo = {
  dropi_product_id: number;
  name: string;
  sku: string | null;
  description: string | null;
  image: string | null;
  suggested_price: number | null;
  cost_price: number | null;
  stock: number | null;
  category: string | null;
  supplier_name: string | null;
  variations: unknown;
};

/**
 * ¿El revendedor tiene su Shopify conectado?
 *
 * Lo usa la tarjeta del catálogo para que su tercer botón diga la verdad: con la tienda conectada
 * ofrece enlazar el producto; sin ella avisa que primero hay que conectarla, en vez de mandar a una
 * pantalla vacía. Es la misma fila de `integrations` que lee Ajustes → Integraciones → Shopify.
 *
 * 🪤 NO LANZA SI FALLA. Que no se pueda leer el estado de una integración no puede dejar sin
 *    catálogo a nadie: en la duda responde «no conectado», que es el texto más útil de los dos.
 */
export async function tieneShopifyConectado(tenantId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('integrations')
    .select('status')
    .eq('tenant_id', tenantId)
    .eq('provider', 'shopify')
    .maybeSingle();
  if (error) return false;
  return (data as { status?: string } | null)?.status === 'connected';
}

/**
 * El catálogo entero en una llamada. Hoy son decenas de productos de un proveedor;
 * el filtrado y la paginación los hace la pantalla, igual que con Aliclik. Cuando
 * haya varios proveedores y miles de productos, esto pasa a filtrar en el servidor
 * como ya hace Dropi — el patrón ya está escrito al lado.
 */
export async function fetchDropCatalogo(tenantId: string): Promise<DropCatalogoRow[]> {
  // 🪤 El tenant con el que se está trabajando se manda explícito. Sin él, la función esconde los
  // productos de TODOS los tenants del usuario — y quien maneja varias tiendas (una agencia, o el
  // founder, que pertenece a las seis) se quedaba mirando un catálogo vacío sin explicación.
  const { data, error } = await supabase.rpc('fn_drop_catalogo', { p_tenant: tenantId });
  if (error) throw error;
  return (data ?? []) as DropCatalogoRow[];
}

/**
 * Importa un producto del catálogo propio.
 *
 * 🔒 Se manda SOLO el `catalogo_id`. El precio mayorista, el sugerido, el nombre y
 * el stock los lee el servidor de la base. Si los mandara el navegador, cualquiera
 * podría importar a S/ 0 y el costo del revendedor sería mentira desde el día uno.
 */
export function importProductFromSmartyDrop(
  tenantId: string,
  catalogoId: string,
): Promise<SupplierImportResult> {
  return importFromSupplier(tenantId, 'smartydrop', { catalogo_id: catalogoId });
}

/** Un pedido que el revendedor le hizo a un proveedor. */
export interface PedidoDropMio {
  order_id: string;
  numero: string;
  /** 🪤 Por id, no por nombre: cruzar listas por nombre se rompe con una tilde. */
  proveedor_id: string;
  proveedor: string;
  cuentas: CuentaDeCobro[];
  proveedor_wa: string | null;
  estado: string;
  pago: string;
  envio: string;
  costo_despacho: number;
  total: number;
  pagado: number;
  debe: number;
  /** Estado de la ÚLTIMA declaración de pago: declarado | confirmado | rechazado | null. */
  declaracion: string | null;
  motivo_rechazo: string | null;
  creado: string;
  /**
   * ⭐ DE QUÉ AGENCIA SALE EL PAQUETE. Lo declara el ALMACÉN, no el revendedor: si registra la guía
   * con otro origen, nadie va a llevar el paquete a esa agencia y el pedido queda muerto sin que
   * ningún estado lo diga. El destino lo elige él y no le importa a nadie más.
   * 20261102190000: con la guía emitida, el origen que quedó en la guía (null si no quedó: no se adivina); sin guía, la
   * agencia del almacén que despacha. Ya no la ficha de proveedor de hoy.
   */
  agencia_origen: string | null;
  agencia_direccion: string | null;
  agencia_horario: string | null;
  /** El número de guía, si ya hay. */
  tracking: string | null;
  /** `smarty` la emitió nuestra integración · `externo` la sacó él en el mostrador. */
  registrado_por: string | null;
  /** Quién cargó la venta (founder, 22-oct-2026: el administrador paga, pero quiere ver de qué vendedor es). */
  vendedor: string | null;
}

/**
 * ⭐ LO QUE LA PANTALLA DE ENTREGA NECESITA SABER cuando la venta se le pidió al almacén.
 *
 * POR QUÉ EXISTE. El founder detectó un error de modelo mío: «el registro por Shalom será al cliente
 * final, no al dropshipper». La guía es la ENTREGA DEL REVENDEDOR A SU CLIENTE — nace en su venta,
 * con su cliente como destinatario. Yo la había colgado del pedido MAYORISTA, que es el otro lado
 * del trato.
 *
 * El síntoma se veía: el botón para anotarla aparecía en «Mis pedidos» y no en Entregas, que es
 * donde él fue a buscarlo. Cuando una acción está en la pantalla donde nadie la busca, casi siempre
 * está colgada del documento equivocado.
 *
 * 🚨 EL ORIGEN NO ES NEGOCIABLE. Lo declara el ALMACÉN: si el revendedor registra con otra agencia,
 *    nadie va a llevar el paquete ahí y la guía queda muerta sin que ningún estado lo diga.
 *
 * Devuelve `null` si la venta no pasó por Smarty Drop, que es el caso de casi todas.
 */
/**
 * El plan de cumplimiento de una venta: quién despacha, desde dónde y con qué cuenta.
 *
 * ⭐ SE DERIVA DE LAS LÍNEAS, así que existe desde el primer producto — no hace falta que la venta
 *    esté confirmada ni que el encargo exista. Antes esto se leía de `drop_pedidos`, una fila que
 *    nace recién al confirmar: por eso en el presupuesto no aparecían ni el origen ni la opción de
 *    quién registra. Es el *fulfillment order assignment* de Shopify, calculado antes.
 */
export interface CumplimientoDeVenta {
  proveedor_id: string;
  proveedor: string;
  proveedor_tenant: string;
  /** La agencia del ALMACÉN. No cambia aunque la guía la saque el revendedor con su cuenta. */
  agencia_origen_id: string | null;
  agencia_origen: string | null;
  agencia_direccion: string | null;
  /** `almacen` la saca el proveedor por él · `revendedor` la saca él con su Shalom Pro. */
  registra: 'revendedor' | 'almacen';
  /** Con qué cuenta se piden las tarifas. Una tarifa pertenece a una cuenta, no al aire. */
  cuenta_cotiza: string;
  cuenta_emite: string;
  /** Si esa cuenta existe y está conectada. Si no, no hay cajas ni aéreo/terrestre. */
  puede_cotizar: boolean;
  /** Qué cuenta falta, dicho con nombre: mandar a conectar «Shalom» a secas hace perder una tarde. */
  motivo_sin_cuenta: string | null;
  /**
   * El portal de la cuenta que despacha: con ése se cotiza y de ése salen las cajas.
   *
   * 🚨 Preguntarle el portal al REVENDEDOR caía al default `'pro'`, cuyo catálogo de cajas está
   *    vacío — y sin caja no hay precio. Corporativo es el caso normal (tarifas bajas); Pro es la
   *    excepción del que trae su propia cuenta.
   */
  portal: 'pro' | 'corporativo' | null;
  /**
   * Cómo despacha el almacén: su caja habitual, su modo y quién paga el flete.
   *
   * 🚨 Estos tres se leían de la configuración DEL REVENDEDOR, que está vacía — por eso salía
   *    «Sobre» y el modo del código. El que despacha aporta sus defaults, igual que el resto.
   */
  caja_default: string | null;
  aereo_default: boolean | null;
  flete_default: 'origin' | 'destination' | null;
  pedido_id: string | null;
  tracking: string | null;
}

export async function cumplimientoDeVenta(ventaId: string): Promise<CumplimientoDeVenta | null> {
  const { data, error } = await supabase.rpc('fn_cumplimiento_de_la_venta', { p_venta: ventaId });
  if (error) throw error;
  const filas = (data ?? []) as CumplimientoDeVenta[];
  return filas[0] ?? null;
}


/** Los pedidos que hizo este revendedor. Viven en el tenant del proveedor; se leen por función. */
export async function misPedidosDrop(tenantId: string): Promise<PedidoDropMio[]> {
  const { data, error } = await supabase.rpc('fn_drop_mis_pedidos', { p_revendedor: tenantId });
  if (error) throw error;
  return (data ?? []) as PedidoDropMio[];
}

import type { CuentaDeCobro } from '@/components/dropshipping/ADondeDepositar';
import { laPlataCambio } from '@/lib/dropshipping/laPlataCambio';

/** Métodos con los que se cobra de verdad en este negocio. Los mismos que acepta `order_payments`. */
export const METODOS_DROP = [
  { value: 'yape', label: 'Yape' },
  { value: 'plin', label: 'Plin' },
  { value: 'transfer', label: 'Transferencia' },
  { value: 'cash', label: 'Efectivo' },
] as const;

export type MetodoDrop = (typeof METODOS_DROP)[number]['value'];

/**
 * El revendedor DECLARA que pagó. No es lo mismo que haber pagado.
 *
 * ⭐ Declarar no mueve un sol: sólo crea la fila con su prueba. La plata entra a `order_payments`
 * —la única definición de dinero cobrado en Smarty— cuando el proveedor confirma. Si el comprador
 * pudiera marcar su propio pedido como pagado, cualquiera sube una captura y se lleva mercadería.
 *
 * 🪤 Hace falta el código de operación O la captura: sin ninguno de los dos no hay nada que el
 * proveedor pueda verificar contra su estado de cuenta.
 */
export async function declararPagoDrop(input: {
  /**
   * 🚨 EL TENANT VA EXPLÍCITO. Antes la base lo adivinaba con `limit 1` sobre los tenants del
   * usuario, y con alguien que pertenece a varios eso acredita la plata al negocio equivocado —
   * pasó de verdad: una recarga hecha desde el revendedor se acreditó a Dulti.
   */
  tenantId: string;
  /** El pedido que se paga, o `null` si es una RECARGA de saldo. */
  orderId: string | null;
  metodo: MetodoDrop;
  monto: number;
  referencia?: string;
  comprobanteUrl?: string;
}): Promise<{ pago_id: string; estado: string; numero: string; monto: number; debe: number | null; es_recarga: boolean }> {
  const { data, error } = await supabase.rpc('fn_drop_declarar_pago', {
    p_revendedor: input.tenantId,
    p_order: input.orderId,
    p_metodo: input.metodo,
    p_monto: input.monto,
    p_referencia: input.referencia?.trim() || undefined,
    p_comprobante: input.comprobanteUrl || undefined,
    /*
     * 🪤 NO SE MANDA `p_proveedor`. La recarga es con Smarty, no con un proveedor, así que la base
     * lo ignora — y mandarlo «por si acaso» fue lo que creó la sobrecarga que rompió la pantalla el
     * 24-ago-2026: quedaron dos firmas y PostgREST no pudo elegir.
     */
  });
  if (error) throw new Error(error.message);
  laPlataCambio();
  return data as unknown as { pago_id: string; estado: string; numero: string; monto: number; debe: number | null; es_recarga: boolean };
}

/**
 * El saldo del revendedor con SMARTY. Uno solo, no uno por proveedor.
 *
 * ⭐ LA PLATA ENTRA A SMARTY, NO AL PROVEEDOR. Decisión del founder (23-ago-2026) y corrige un
 * modelo que yo había construido al revés: con el saldo por proveedor, el revendedor tenía que
 * recargar una vez por cada uno, y Smarty no cobraba nada porque el dinero nunca la tocaba.
 *
 * ⚠️ Lo que cuesta, dicho antes de que pase: el día que entre un proveedor que no sea del founder,
 * Smarty va a DEBERLE plata — el mismo mecanismo que genera los cortes de la competencia. La salida
 * no es volver atrás: es que ese proveedor entre con split payments (el procesador divide en el
 * momento del cobro). Está en el plan maestro.
 */
export interface SaldoDrop {
  /** Cuánto tengo. */
  saldo: number;
  /** A dónde depositarle a Smarty. De `tenant_business_profile` de Smarty HQ. */
  cuentas: CuentaDeCobro[];
  /** El promedio de MIS pedidos anteriores — no un número inventado. */
  ticket_promedio: number | null;
  /** Cuántos pedidos me alcanzan. `null` si todavía no hice ninguno. */
  pedidos_alcanzan: number | null;
  movimientos: number;
  /**
   * ⭐ PEDIDOS QUE ESPERAN PLATA MÍA. Sin esto, un revendedor puede crear tres pedidos sin saldo,
   * cerrar el panel y no enterarse: los pedidos existen, el almacén los ve, y no salen.
   *
   * 🪤 «Frenado» es lo que DEBE y NO tiene comprobante declarado, no «lo que no está pagado». Con el
   *    comprobante mandado la pelota es nuestra, y contarlo ahí lo mandaría a pagar dos veces.
   */
  pedidos_frenados: number;
  /** Cuánto falta en total para destrabarlos. */
  falta_total: number;
  /**
   * Cuántos de esos `pedidos_frenados` son VENTAS esperando saldo, no encargos impagos.
   *
   * ⭐ Desde que confirmar la venta crea el encargo y lo cobra, lo que queda esperando es la venta:
   *    no hay ningún pedido que mirar. Sin este corte la pantalla diría «pedidos» de algo que no lo
   *    es, y mandaría a Mis compras, donde una venta esperando no aparece.
   */
  ventas_esperando: number;
}

/** El saldo del revendedor. Devuelve una sola fila. */
export async function miSaldoDrop(tenantId: string): Promise<SaldoDrop | null> {
  const { data, error } = await supabase.rpc('fn_drop_mi_saldo', { p_revendedor: tenantId });
  if (error) throw error;
  const filas = (data ?? []) as SaldoDrop[];
  return filas[0] ?? null;
}

/**
 * ⭐ EL PUENTE QUE FALTABA: lo que le cuesta al revendedor pedirle al almacén los productos de una
 * venta SUYA.
 *
 * POR QUÉ EXISTE. El founder, usándolo: «¿dónde carajos ve el dropshipper cuánto debe pagar de su
 * billetera por el pedido que está haciendo al proveedor? ¿cuánto se descuenta? ¿cuánto queda? No se
 * ve. Por otro lado no se ve el pedido que llegue al proveedor para que lo aliste».
 *
 * No era un problema de pantalla: **la venta y el pedido al almacén eran dos cosas sin ningún
 * vínculo**. El revendedor vendía y el sistema no hacía nada; para que el almacén se enterara tenía
 * que ir a otro módulo y volver a pedir el mismo producto a mano.
 *
 * Devuelve `null` cuando la venta no tiene productos de Smarty Drop — que es el caso normal en
 * cualquier tienda que no sea revendedora.
 */
export interface PrevioDeVenta {
  proveedor_id: string;
  proveedor: string;
  items: Array<{
    catalogo_id: string;
    producto: string;
    cantidad: number;
    precio: number;
    subtotal: number;
  }>;
  /** Lo que valen los productos al POR MAYOR — no lo que él le cobró a su cliente. */
  costo_productos: number;
  despacho: number;
  total: number;
  saldo: number;
  alcanza: boolean;
  falta: number;
  /** Si ya se pidió: el pedido mayorista que salió de esta venta. */
  pedido_id: string | null;
  pedido_numero: string | null;
  pedido_pago: string | null;
  /**
   * 🚨 LO QUE EL ALMACÉN NO PUEDE CUBRIR. Prometerle stock inexistente a alguien que YA le vendió a
   * su cliente es el riesgo número uno de este modelo: la base bloquea el pedido, y esto existe para
   * que el bloqueo no sea una sorpresa al apretar el botón.
   *
   * 🪤 Dice CUÁNTO HAY, no «sin stock»: lo primero deja decidir, lo segundo obliga a preguntar por
   *    WhatsApp — que es de donde estamos sacando estas conversaciones.
   */
  /**
   * Lo que impide pedirle esta venta al almacén. `motivo` distingue las dos causas: el proveedor no
   * tiene suficiente, o dejó de publicar el producto — que se arreglan de maneras distintas.
   */
  sin_stock: Array<{ producto: string; pediste: number; hay: number; motivo?: 'sin_stock' | 'no_publicado' }>;
}

export async function previoDeVenta(ventaId: string): Promise<PrevioDeVenta | null> {
  const { data, error } = await supabase.rpc('fn_drop_previo_de_venta', { p_venta: ventaId });
  if (error) throw error;
  const filas = (data ?? []) as PrevioDeVenta[];
  return filas[0] ?? null;
}

/*
 * ⭐ EL MISMO PREVIO, PEDIDO POR LA CLAVE QUE TRAE LA URL (número o uuid).
 *
 *    El founder: «esta opción de pagar almacén demora en aparecer, ¿a qué se debe?».
 *
 * 🪤 NO ERA LA BASE. Medido en producción: la consulta tarda 5,8 ms y resolver la venta 0,3 ms.
 *    Lo que se esperaba eran dos VIAJES DE RED en fila — la URL trae el NÚMERO de la venta
 *    (`/ventas/orden/S-16339`), así que el panel pedía la venta para traducirlo a uuid y RECIÉN
 *    entonces podía pedir el previo. La tarjeta era la segunda de una cola.
 *
 *    Con esto los dos pedidos salen a la vez. No se hace más rápido nada: se dejan de esperar.
 */
export async function previoDeVentaPorClave(clave: string, tenantId?: string | null): Promise<PrevioDeVenta | null> {
  const { data, error } = await supabase.rpc('fn_drop_previo_por_clave', { p_clave: clave, p_tenant: tenantId ?? null }); // 🔒 el número es único POR NEGOCIO
  if (error) throw error;
  const filas = (data ?? []) as PrevioDeVenta[];
  return filas[0] ?? null;
}

/**
 * Manda la venta al almacén y, si el saldo alcanza, la paga en el mismo acto.
 *
 * 🪤 EL PAGO SE INTENTA, NO SE EXIGE. Si no alcanza, el pedido YA existe y el almacén ya lo ve:
 *    fallar entero obligaría a rehacer todo después de recargar. Vuelve con `pago` en null y el
 *    panel muestra cuánto falta.
 */
export async function pedirVentaAlAlmacen(ventaId: string, pagarConSaldo = true) {
  const { data, error } = await supabase.rpc('fn_drop_pedir_para_venta', {
    p_venta: ventaId,
    p_pagar: pagarConSaldo,
  });
  if (error) throw error;
  laPlataCambio();
  return data as {
    order_id: string;
    numero: string;
    total: number;
    proveedor: string;
    saldo: number;
    pago: { pagado?: number; motivo?: string } | null;
  };
}


/** Una recarga que el revendedor declaró: lo que ve mientras espera. */
export interface RecargaDrop {
  pago_id: string;
  metodo: string;
  monto: number;
  referencia: string | null;
  comprobante_url: string | null;
  estado: 'declarado' | 'confirmado' | 'rechazado';
  motivo: string | null;
  creado: string;
  revisado_en: string | null;
}

/**
 * Las recargas del revendedor, con su estado.
 *
 * 🚨 SIN ESTO NO HABÍA NINGUNA SEÑAL entre «enviar» y «te lo confirmaron»: una recarga declarada
 * todavía no es un movimiento del saldo —el movimiento nace al confirmarla—, así que el revendedor
 * mandaba plata y la pantalla actuaba como si no hubiera pasado. Es la forma más rápida de que
 * mande el comprobante otra vez por WhatsApp.
 */
export async function misRecargasDrop(tenantId: string): Promise<RecargaDrop[]> {
  const { data, error } = await supabase.rpc('fn_drop_mis_recargas', { p_revendedor: tenantId });
  if (error) throw error;
  return (data ?? []) as RecargaDrop[];
}

/** Un movimiento del saldo: de dónde salió cada sol. */
export interface MovimientoDrop {
  id: string;
  /**
   * El tipo del asiento del libro que movió la billetera (la vista `drop_saldo_movimientos` es el libro), más
   * `pago_directo`: un encargo pagado por Yape que pasó por la billetera sin cambiarla (20261009230000).
   * `devolucion` es el nombre viejo del reintegro; el libro hoy anota `reintegro`. Lo que cobra, lo que se le
   * descuenta y lo que retira: `rendicion`, `no_llego`, `no_entregado`, `comision`, `manejo`, `liquidacion`.
   */
  tipo: 'recarga' | 'consumo' | 'reintegro' | 'devolucion' | 'ajuste' | 'pago' | 'pago_directo'
    | 'rendicion' | 'no_llego' | 'no_entregado' | 'comision' | 'manejo' | 'liquidacion';
  monto: number;
  nota: string | null;
  /** El número del pedido que lo consumió, si vino de uno. */
  numero: string | null;
  /** A qué proveedor se le fue ese dinero. `null` en las recargas: son con Smarty. */
  proveedor: string | null;
  creado: string;
  /** El pedido que causó el movimiento, para poder ir hasta él de un clic. */
  order_id: string | null;
  /**
   * En cuánto quedó el saldo DESPUÉS de este movimiento.
   *
   * 🪤 Un extracto bancario lo trae y no es adorno: sin él no se puede auditar hacia atrás. Si el
   *    saldo de hoy no cuadra, la única forma de encontrar dónde se torció es ver en cuánto quedó
   *    después de cada línea.
   */
  saldo_despues: number;
}

/**
 * El historial del saldo con un proveedor.
 *
 * 🪤 Un saldo sin historial es un número que hay que creer. La primera pregunta de cualquiera que
 * ve un saldo raro es «¿de dónde salió?» — si la pantalla no la contesta, la contesta un WhatsApp.
 */
export async function movimientosDrop(tenantId: string): Promise<MovimientoDrop[]> {
  const { data, error } = await supabase.rpc('fn_drop_mis_movimientos', { p_revendedor: tenantId });
  if (error) throw error;
  return (data ?? []) as MovimientoDrop[];
}

/**
 * Paga un pedido con el saldo. Sin comprobante y sin espera: el proveedor ya cobró ese dinero
 * cuando confirmó la recarga.
 */
export async function pagarPedidoConSaldo(
  orderId: string,
): Promise<{ order_id: string; pagado: number; saldo: number; pedido: string }> {
  const { data, error } = await supabase.rpc('fn_drop_pagar_con_saldo', { p_order: orderId });
  if (error) throw new Error(error.message);
  laPlataCambio();
  return data as unknown as { order_id: string; pagado: number; saldo: number; pedido: string };
}

/**
 * Un cobro que alguien declaró: recarga de saldo o pago de un pedido, en cualquier estado.
 *
 * 🪤 A DIFERENCIA DE `PagoDropPorRevisar`, ÉSTE TRAE TAMBIÉN LO YA REVISADO. Una bandeja que sólo
 * muestra lo pendiente sirve para trabajar, no para responder — y la primera pregunta que llega por
 * WhatsApp es siempre sobre algo que YA se confirmó o se rechazó.
 */
export interface CobroDrop {
  pago_id: string;
  order_id: string | null;
  concepto: string;
  revendedor: string;
  metodo: string;
  monto: number;
  referencia: string | null;
  comprobante_url: string | null;
  estado: 'declarado' | 'confirmado' | 'rechazado';
  motivo: string | null;
  es_recarga: boolean;
  creado: string;
  revisado_en: string | null;
  /** Lo que escribió el drop, si Smarty lo corrigió con lo que llegó (`monto` es lo acreditado). 20261013190000. */
  monto_declarado: number | null;
  motivo_correccion: string | null;
  corregido_en: string | null;
}

/** Todo lo que le declararon a este tenant: recargas si es la plataforma, pagos si es proveedor. */
export async function cobrosRecibidosDrop(tenantId: string): Promise<CobroDrop[]> {
  const { data, error } = await supabase.rpc('fn_drop_cobros_recibidos', { p_tenant: tenantId });
  if (error) throw error;
  return (data ?? []) as CobroDrop[];
}

/** Un pago que alguien declaró y el proveedor todavía no miró. */
export interface PagoDropPorRevisar {
  pago_id: string;
  /** `null` en una recarga de saldo: no hay pedido, la plata es para Smarty. */
  order_id: string | null;
  numero: string;
  revendedor: string;
  metodo: string;
  monto: number;
  referencia: string | null;
  comprobante_url: string | null;
  total_pedido: number | null;
  /** Una recarga la confirma Smarty; un pago de pedido, el dueño del catálogo. */
  es_recarga: boolean;
  declarado_en: string;
}

/** La bandeja del proveedor: lo que le dicen que le pagaron y él todavía no reconoció. */
export async function pagosDropPorRevisar(tenantId: string): Promise<PagoDropPorRevisar[]> {
  const { data, error } = await supabase.rpc('fn_drop_pagos_por_revisar', { p_tenant: tenantId });
  if (error) throw error;
  return (data ?? []) as PagoDropPorRevisar[];
}

/**
 * Smarty corrige una recarga con lo que LLEGÓ (20261013190000): por revisar, la confirma por ese monto; ya confirmada,
 * asienta la diferencia (el libro no se edita). El negocio recibe un aviso con cuánto quedó y el motivo.
 */
export async function corregirRecargaDrop(pagoId: string, monto: number, motivo: string): Promise<{ antes: number; ahora: number; diferencia: number }> {
  const { data, error } = await supabase.rpc('fn_drop_corregir_recarga', { p_pago: pagoId, p_monto: monto, p_motivo: motivo.trim() });
  if (error) throw new Error(error.message);
  laPlataCambio();
  const d = data as { antes: number; ahora: number; diferencia: number };
  return { antes: Number(d.antes), ahora: Number(d.ahora), diferencia: Number(d.diferencia) };
}

/**
 * El proveedor reconoce (o rechaza) el pago. Este es el único punto por el que entra plata.
 *
 * 🪤 Confirmar dos veces no cobra dos veces: la base escribe con `idempotency_key` único. Volver a
 * confirmar algo ya confirmado devuelve `sin_cambios`, no un error — dos pestañas abiertas no son
 * un problema del usuario.
 */
export async function revisarPagoDrop(
  pagoId: string, aprobar: boolean, motivo?: string,
): Promise<{ estado: string; pedido?: string; sin_cambios?: boolean }> {
  const { data, error } = await supabase.rpc('fn_drop_confirmar_pago', {
    p_pago: pagoId,
    p_aprobar: aprobar,
    p_motivo: motivo?.trim() || undefined,
  });
  if (error) throw new Error(error.message);
  laPlataCambio();
  return data as unknown as { estado: string; pedido?: string; sin_cambios?: boolean };
}
